import type { TtqSessionParticleEntry } from '../../services/ttqService';
import { buildPapNodeSummary } from './papDisplay';
import { tasteVerdictLabel } from '../../utils/ttqDisplay';

export type TtqPipelineFlowStepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export type TtqPipelineFlowStep = {
  order: number;
  label: string;
  status: TtqPipelineFlowStepStatus;
  detail?: string;
};

const STEP_DEFS = [
  { order: 1, label: '选颗粒' },
  { order: 2, label: '品位分析' },
  { order: 3, label: '框架匹配' },
  { order: 4, label: '隐性能力包' },
  { order: 5, label: '品起手式' },
  { order: 6, label: '品突破点' },
] as const;

function papStepDetail(entry: TtqSessionParticleEntry): string | undefined {
  if (entry.papFailure?.message) return entry.papFailure.message;
  const summary = buildPapNodeSummary(entry.pap);
  if (summary) return summary;
  const admission = entry.pap?.admission?.status;
  if (admission === 'ready') return 'Session 副本就绪';
  if (admission === 'partial') return '部分就绪';
  return undefined;
}

function buildStepDetail(order: number, entry: TtqSessionParticleEntry): string | undefined {
  switch (order) {
    case 1:
      return '已加入会话';
    case 2:
      return entry.taste
        ? `${tasteVerdictLabel(entry.taste.verdict)}${entry.taste.rationale ? ` · ${entry.taste.rationale}` : ''}`
        : undefined;
    case 3: {
      if (entry.frameworkMatches == null) return undefined;
      const count = entry.frameworkMatches.length;
      if (count <= 0) return '无框架匹配';
      const first = entry.frameworkMatches[0]?.reason?.trim();
      return first ? `${count} 项 · ${first}` : `${count} 项匹配`;
    }
    case 4:
      return papStepDetail(entry);
    case 5:
      return entry.openingTaste?.suggestedFirstStep?.trim()
        || entry.openingTaste?.narrative?.trim()
        || undefined;
    case 6: {
      const item = entry.breakthroughTaste?.items?.[0]?.description?.trim();
      if (item) return item;
      const count = entry.breakthroughTaste?.breakthroughCount;
      return count != null && count > 0 ? `${count} 个突破点` : undefined;
    }
    default:
      return undefined;
  }
}

function resolveStepStatus(order: number, entry: TtqSessionParticleEntry): TtqPipelineFlowStepStatus {
  const { pipelineStatus } = entry;

  if (order === 1) return 'done';

  if (pipelineStatus === 'pap_failed') {
    return order === 4 ? 'failed' : 'skipped';
  }

  if (order === 4) {
    if (pipelineStatus === 'pending') return 'pending';
    if (pipelineStatus === 'pap_running') return 'running';
    if (entry.pap) return 'done';
    return 'pending';
  }

  const fieldDone = (
    (order === 2 && Boolean(entry.taste))
    || (order === 3 && entry.frameworkMatches != null)
    || (order === 5 && Boolean(entry.openingTaste))
    || (order === 6 && Boolean(entry.breakthroughTaste))
  );

  if (pipelineStatus === 'done') {
    return fieldDone ? 'done' : 'pending';
  }

  if (pipelineStatus === 'analyzing') {
    return fieldDone ? 'done' : 'running';
  }

  if (!entry.pap) return 'pending';
  return 'pending';
}

export function buildTtqPipelineFlowSteps(entry: TtqSessionParticleEntry): TtqPipelineFlowStep[] {
  return STEP_DEFS.map(({ order, label }) => ({
    order,
    label,
    status: resolveStepStatus(order, entry),
    detail: buildStepDetail(order, entry),
  }));
}

export function hasTtqPipelineFlowSteps(entry: TtqSessionParticleEntry | null | undefined): boolean {
  return Boolean(entry);
}
